"use client";

import { intersection } from "@utils/array";
import { useLiveQuery } from "dexie-react-hooks";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import React, { ComponentPropsWithRef, forwardRef, useContext, useEffect, useMemo, useRef } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";

import { controlledCheckbox, parseFilters } from "@/components/ui/Form/Filters/FilterUtils";
import { ANY_VALUE, DEFAULT_TYPE, SearchContext } from "@/context/SearchContext";
import { UserContext } from "@/context/UserContext";

export interface NumbersBaseProps extends ComponentPropsWithRef<"input"> {
  name: string;
  filter: string;
  displayInSearchBar?: boolean;
  values?: (string | number)[] | undefined;
  field?: ControllerRenderProps;
  fieldState?: ControllerFieldState;
  availableValues?: (string | number)[] | undefined;
}
const baseContainerClass = "filter-content overflow-x-auto pb-xs gap-xs";
const paddingClasses = (displayInSearchBar: boolean) => {
  return displayInSearchBar ? "" : "pt-xs px-m";
};
const labelClasses = `border border-gray-20 rounded-full px-l py-[6px] flex flex-col items-center justify-center`;
const NumberBase = forwardRef<HTMLInputElement, NumbersBaseProps>(
  ({ name, filter, displayInSearchBar = false, field, fieldState, values, availableValues, ...rest }, ref) => {
    availableValues = useMemo(() => {
      return availableValues?.filter((item) => item !== "0") ?? [];
    }, [availableValues]);
    const searchParams = useSearchParams();
    const anyID = `filter-${name}-any`;
    const t = useTranslations();
    const checkboxes = useRef<(HTMLInputElement | null)[]>([]);
    const { params } = useContext(SearchContext);
    const { currency, measurement } = useContext(UserContext);
    const dbValues = useLiveQuery(async () => {
      // return await parseFilters(params?.type ?? DEFAULT_TYPE, ["bedrooms"], currency, measurement);
      const filters = await parseFilters(params?.type ?? DEFAULT_TYPE, ["bedrooms"], currency, measurement);
      if (filters?.bedrooms) {
        // Move "STD" to the beginning of the array if it exists
        const stdValues = filters.bedrooms.filter((item: any) => item === "STD");
        const otherValues = filters.bedrooms.filter((item: any) => item !== "STD" && item !== "0");
        filters.bedrooms = [...stdValues, ...otherValues]; // Combine "STD" first, then other values
      }
      return filters;
    }, [searchParams.get("type"), currency, measurement]);
    const valueGroups = useMemo(() => {
      const firstGroup = dbValues?.bedrooms?.slice(0, 7) ?? [];
      const secondGroup: (string | number)[] = dbValues?.bedrooms?.slice(7)?.filter((item: string | number) => item !== "STD") ?? [];
      return [firstGroup, secondGroup];
    }, [values, dbValues?.bedrooms]);

    return (
      <div
        className={`${baseContainerClass} flex ${displayInSearchBar ? "flex-nowrap" : "flex-wrap"} pt-xs px-m ${paddingClasses(displayInSearchBar)}`}
      >
        <div className="filter-number">
          <input
            id={anyID}
            onChange={(e) => {
              if (e.currentTarget.checked) {
                checkboxes.current.forEach((input) => {
                  if (input && input.type === "checkbox") {
                    input.checked = false;
                  }
                });
              }
              field?.onChange([]);
            }}
            name={filter}
            className="appearance-none hidden"
            type="checkbox"
            value={ANY_VALUE}
          />
          <label className={`${labelClasses} ${field?.value?.length > 0 ? "" : "bg-gold-gradient"}`} htmlFor={anyID}>
            {t("filter.label.any")}
          </label>
        </div>
        {valueGroups[0]?.map((value: string | number, index: number) => {
          const filedUID = `filter-${name}-${value}`;
          const isStudio = value === "0" || value === "STD";
          if (name === "bathrooms" && (isStudio || value === "STD")) {
            return;
          }
          return (
            <div className="filter-number" key={filedUID}>
              <input
                ref={(el) => {
                  checkboxes.current[index] = el;
                }}
                {...rest}
                id={filedUID}
                className="appearance-none hidden"
                type="checkbox"
                checked={field?.value?.includes(isStudio ? "STD" : (value as string)) ?? false} // Use "std" for Studio
                value={isStudio ? "STD" : value} // Set "std" as the value for Studio
                onChange={(event) => controlledCheckbox(field, event)}
                //disabled={availableValues && availableValues.length > 0 ? !availableValues?.includes(isStudio ? "STD" : value) : false}
              />
              <label
                className={`transition-colors ${
                  availableValues && availableValues.length > 0 && availableValues.includes(isStudio ? "STD" : value)
                    ? "cursor-pointer"
                    : "cursor-pointer"
                } ${labelClasses} ${field?.value?.includes(isStudio ? "STD" : (value as string)) ? "bg-gold-gradient" : ""}`}
                htmlFor={filedUID}
              >
                {/* {!["0"].includes(value as string) ? value : t("bedrooms.studio")} */}
                {!["STD"].includes(value as string) ? value : t("bedrooms.studio")}
              </label>
            </div>
          );
        })}
        {valueGroups[1]?.length > 0 && (
          <div className="filter-number" key={`filter-${name}-more`}>
            <input
              ref={(el) => {
                checkboxes.current[7] = el;
              }}
              {...rest}
              id={`filter-${name}-more`}
              className="appearance-none hidden"
              type="checkbox"
              checked={field?.value?.includes(valueGroups[1][0]) ?? false}
              value={valueGroups[1].map((item: string | number) => item.toString())}
              onChange={(event) => controlledCheckbox(field, event)}
              //disabled={availableValues && availableValues.length > 0 ? intersection(valueGroups[1], availableValues).length === 0 : false}
            />
            <label
              className={`transition-colors text-nowrap ${availableValues && availableValues.length > 0 && intersection(valueGroups[1], availableValues).length > 0 ? "cursor-pointer" : "cursor-pointer"} ${labelClasses} ${valueGroups[1].filter((value: string | number) => field?.value?.includes(value as any)).length > 0 ? "bg-gold-gradient" : ""}`}
              htmlFor={`filter-${name}-more`}
            >
              {`7 +`}
            </label>
          </div>
        )}
      </div>
    );
  },
);
NumberBase.displayName = "NumberBase";
export default NumberBase;
